// src/App.tsx
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CompanyProvider, useCompany } from './contexts/CompanyContext';
import { AppThemeProvider } from './theme/ThemeProvider';
import { AppLoadingProvider, useAppLoading } from './contexts/appLoadingContext';
import ProtectedRoute from './components/ProtectedRoute';
import RoleProtectedRoute from './admin/components/RoleProtectedRoute';
import { queryClient } from './lib/queryClient';
import AppLoader from './admin/components/appLoader';
import { getDefaultPathForPermissions } from './admin/components/rolePermissions';

// Admin Components
import AdminLayout from './admin/components/AdminLayout';
import AdminLogin from './admin/pages/Login';
import AdminRegister from './admin/pages/Register';
import AdminDashboard from './admin/pages/Dashboard';

// Page Components
import ProductsPage from './admin/pages/product/Products';
import LabelPrinting from './admin/pages/labels/LabelPrinting';
import OrdersPage from './admin/pages/order/Orders';
import CategoryManagement from './admin/pages/category/CategoryManagement';
import VariantManagement from './admin/pages/variant/VariantManagement';
import WarehousesPage from './admin/pages/warehouse/Warehouses';
import UserManagement from './admin/pages/users/users';
import UserProfile from './admin/pages/users/userProfile';
import Dashboard from './admin/pages/dashboard/dashboard';
import CompanyProfile from './admin/pages/company/CompanyProfile';
import BlogManagement from './admin/pages/blogs/BlogManagement';
import ShippingManagement from './admin/pages/shipping/ShippingManagement';
import ContactMessagesManagement from './admin/pages/messages/ContactMessagesManagement';
import ReviewsManagement from './admin/pages/review/ReviewsManagement';
import PurchaseOrdersManagement from './admin/pages/purchase-orders/PurchaseOrdersManagement';
import SuppliersManagement from './admin/pages/supplier/SuppliersManagement';
import CostsManagement from './admin/pages/cost/CostsManagement';
import BrandManagement from './admin/pages/brand/BrandManagement';
import ReportManagement from './admin/pages/report/ReportManagement';
import ReturnsManagement from './admin/pages/purchase-orders/ReturnsManagement';
import ForgotPassword from './admin/pages/Forgotpasswordfirebase';
import ReturnManagement from './admin/pages/return/ReturnManagement';
import NewsletterManagement from './admin/pages/Newsletter/Newslettermanagement';
import InitialAdminSetup from './admin/pages/users/InitialAdminSetup';
import BannerManagement from './admin/pages/banner/BannerManagement';
import RoleManagement from './admin/pages/roles/RoleManagement';
import CustomersManagement from './admin/pages/customers/CustomersManagement';

// ── EarlyLoader ───────────────────────────────────────────────────────────────
/**
 * Renders immediately on first paint — before AuthProvider / CompanyProvider
 * have mounted. Uses generic fallbacks since company data isn't available yet.
 * Disappears the moment isAppLoading flips to false.
 */
const EarlyLoader: React.FC = () => {
  const { isAppLoading } = useAppLoading();
  if (!isAppLoading) return null;
  return <AppLoader companyName="Loading" logoUrl="/asset/images/logo/logo.png" />;
};

// ── AppReadyGate ──────────────────────────────────────────────────────────────
/**
 * Watches auth + company loading state and flips isAppLoading → false
 * once both providers have finished their initial data fetch.
 */
const AppReadyGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { profileLoading } = useAuth();
  const { loading: companyLoading } = useCompany();
  const { setAppLoading } = useAppLoading();

  useEffect(() => {
    if (!profileLoading && !companyLoading) {
      const timer = setTimeout(() => setAppLoading(false), 400);
      return () => clearTimeout(timer);
    }
  }, [profileLoading, companyLoading, setAppLoading]);

  return <>{children}</>;
};

// ── LoaderOverlay ─────────────────────────────────────────────────────────────
const LoaderOverlay: React.FC = () => {
  const { isAppLoading } = useAppLoading();
  const { companyProfile } = useCompany();
  const logoUrl = companyProfile?.logo || '/asset/images/logo/logo.png';

  if (!isAppLoading) return null;

  return <AppLoader companyName={companyProfile?.companyName} logoUrl={logoUrl} />;
};

// ── Smart default redirect based on live permissions ─────────────────────────
// For super-admin, isSuperAdmin is true so getDefaultPathForPermissions returns
// '/admin/dashboard' immediately without checking any permission codes.
const AdminDefaultRedirect: React.FC = () => {
  const { userProfile, profileLoading, isSuperAdmin } = useAuth();
  if (profileLoading) return null;
  const permissions = userProfile?.permissions ?? [];
  return <Navigate to={getDefaultPathForPermissions(permissions, isSuperAdmin)} replace />;
};

// ── App ───────────────────────────────────────────────────────────────────────
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppThemeProvider>
        <AppLoadingProvider>
          {/*
            EarlyLoader sits OUTSIDE AuthProvider & CompanyProvider.
            It mounts immediately on first paint, eliminating the blank-screen
            flash that occurred while the inner providers were still booting.
          */}
          <EarlyLoader />

          <AuthProvider>
            <CompanyProvider>
              <AppReadyGate>
                {/*
                  LoaderOverlay replaces EarlyLoader visually once
                  CompanyProvider is available. Both share isAppLoading so
                  there's no double flash.
                */}
                <LoaderOverlay />

                <Router>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/" element={<AdminLogin />} />
                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route path="/admin/register" element={<AdminRegister />} />
                    <Route path="/admin/forgot-password" element={<ForgotPassword />} />

                    {/* Protected admin routes */}
                    <Route
                      path="/admin"
                      element={
                        <ProtectedRoute>
                          <AdminLayout />
                        </ProtectedRoute>
                      }
                    >
                      {/* Smart redirect: super-admin → dashboard, others → first permitted page */}
                      <Route index element={<AdminDefaultRedirect />} />

                      {/* Each route is individually guarded by RoleProtectedRoute.
                          Super-admin passes through every RoleProtectedRoute immediately
                          because isSuperAdmin === true short-circuits the check. */}
                      <Route
                        path="dashboard"
                        element={
                          <RoleProtectedRoute path="/admin/dashboard">
                            <Dashboard />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="users"
                        element={
                          <RoleProtectedRoute path="/admin/users">
                            <UserManagement />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="customers"
                        element={
                          <RoleProtectedRoute path="/admin/customers">
                            <CustomersManagement />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="roles"
                        element={
                          <RoleProtectedRoute path="/admin/roles">
                            <RoleManagement />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="orders"
                        element={
                          <RoleProtectedRoute path="/admin/orders">
                            <OrdersPage />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="return"
                        element={
                          <RoleProtectedRoute path="/admin/return">
                            <ReturnManagement />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="products"
                        element={
                          <RoleProtectedRoute path="/admin/products">
                            <ProductsPage />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="labels"
                        element={
                          <RoleProtectedRoute path="/admin/labels">
                            <LabelPrinting />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="category"
                        element={
                          <RoleProtectedRoute path="/admin/category">
                            <CategoryManagement />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="variant"
                        element={
                          <RoleProtectedRoute path="/admin/variant">
                            <VariantManagement />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="brand"
                        element={
                          <RoleProtectedRoute path="/admin/brand">
                            <BrandManagement />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="warehouse"
                        element={
                          <RoleProtectedRoute path="/admin/warehouse">
                            <WarehousesPage />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="shipping"
                        element={
                          <RoleProtectedRoute path="/admin/shipping">
                            <ShippingManagement />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="purchase-order"
                        element={
                          <RoleProtectedRoute path="/admin/purchase-order">
                            <PurchaseOrdersManagement />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="inventory-return"
                        element={
                          <RoleProtectedRoute path="/admin/inventory-return">
                            <ReturnsManagement />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="suplier"
                        element={
                          <RoleProtectedRoute path="/admin/suplier">
                            <SuppliersManagement />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="reports"
                        element={
                          <RoleProtectedRoute path="/admin/reports">
                            <ReportManagement />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="costs"
                        element={
                          <RoleProtectedRoute path="/admin/costs">
                            <CostsManagement />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="messages"
                        element={
                          <RoleProtectedRoute path="/admin/messages">
                            <ContactMessagesManagement />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="reviews"
                        element={
                          <RoleProtectedRoute path="/admin/reviews">
                            <ReviewsManagement />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="blogs"
                        element={
                          <RoleProtectedRoute path="/admin/blogs">
                            <BlogManagement />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="newsletter"
                        element={
                          <RoleProtectedRoute path="/admin/newsletter">
                            <NewsletterManagement />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="banner"
                        element={
                          <RoleProtectedRoute path="/admin/banner">
                            <BannerManagement />
                          </RoleProtectedRoute>
                        }
                      />

                      {/* Always-allowed routes (no permission check) */}
                      <Route path="profile" element={<UserProfile />} />
                      <Route path="settings" element={<CompanyProfile />} />

                      {/* Fallback inside admin */}
                      <Route path="404" element={<AdminDashboard />} />
                      <Route path="*" element={<AdminDefaultRedirect />} />
                    </Route>

                    {/* Global fallback */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Router>
              </AppReadyGate>
            </CompanyProvider>
          </AuthProvider>
        </AppLoadingProvider>
      </AppThemeProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;


// new-----------------
/// // src/App.tsx
// import React, { useEffect } from 'react';
// import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
// import { QueryClientProvider } from '@tanstack/react-query';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
// import { AuthProvider, useAuth } from './contexts/AuthContext';
// import { CompanyProvider, useCompany } from './contexts/CompanyContext';
// import { AppThemeProvider } from './theme/ThemeProvider';
// import { AppLoadingProvider, useAppLoading } from './contexts/appLoadingContext';
// import ProtectedRoute from './components/ProtectedRoute';
// import RoleProtectedRoute from './admin/components/RoleProtectedRoute';
// import { queryClient } from './lib/queryClient';
// import AppLoader from './admin/components/appLoader';
// import { getDefaultPathForRole } from './admin/components/rolePermissions';
// import { UserRole } from './admin/pages/users/userService';

// // Admin Components
// import AdminLayout from './admin/components/AdminLayout';
// import AdminLogin from './admin/pages/Login';
// import AdminRegister from './admin/pages/Register';
// import AdminDashboard from './admin/pages/Dashboard';

// // Page Components
// import ProductsPage from './admin/pages/product/Products';
// import OrdersPage from './admin/pages/order/Orders';
// import CategoryManagement from './admin/pages/category/CategoryManagement';
// import VariantManagement from './admin/pages/variant/VariantManagement';
// import WarehousesPage from './admin/pages/warehouse/Warehouses';
// import UserManagement from './admin/pages/users/users';
// import UserProfile from './admin/pages/users/userProfile';
// import Dashboard from './admin/pages/dashboard/dashboard';
// import CompanyProfile from './admin/pages/company/CompanyProfile';
// import BlogManagement from './admin/pages/blogs/BlogManagement';
// import ShippingManagement from './admin/pages/shipping/ShippingManagement';
// import ContactMessagesManagement from './admin/pages/messages/ContactMessagesManagement';
// import ReviewsManagement from './admin/pages/review/ReviewsManagement';
// import PurchaseOrdersManagement from './admin/pages/purchase-orders/PurchaseOrdersManagement';
// import SuppliersManagement from './admin/pages/supplier/SuppliersManagement';
// import BrandManagement from './admin/pages/brand/BrandManagement';
// import ReportManagement from './admin/pages/report/ReportManagement';
// import ReturnsManagement from './admin/pages/purchase-orders/ReturnsManagement';
// import ForgotPassword from './admin/pages/Forgotpasswordfirebase';
// import ReturnManagement from './admin/pages/return/ReturnManagement';
// import NewsletterManagement from './admin/pages/Newsletter/Newslettermanagement';
// import InitialAdminSetup from './admin/pages/users/InitialAdminSetup';
// import BannerManagement from './admin/pages/banner/BannerManagement';
// import RoleManagement from './admin/pages/roles/RoleManagement';

// // ── EarlyLoader ───────────────────────────────────────────────────────────────
// /**
//  * Renders immediately on first paint — before AuthProvider / CompanyProvider
//  * have mounted. Uses generic fallbacks since company data isn't available yet.
//  * Disappears the moment isAppLoading flips to false.
//  */
// const EarlyLoader: React.FC = () => {
//   const { isAppLoading } = useAppLoading();
//   if (!isAppLoading) return null;
//   return (
//     <AppLoader
//       companyName="Loading"
//       logoUrl="/asset/images/logo/logo.png"
//     />
//   );
// };

// // ── AppReadyGate ──────────────────────────────────────────────────────────────
// /**
//  * Watches auth + company loading state and flips isAppLoading → false
//  * once both providers have finished their initial data fetch.
//  */
// const AppReadyGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
//   const { profileLoading } = useAuth();
//   const { loading: companyLoading } = useCompany();
//   const { setAppLoading } = useAppLoading();

//   useEffect(() => {
//     if (!profileLoading && !companyLoading) {
//       // Small delay so the loader doesn't flash out too abruptly
//       const timer = setTimeout(() => setAppLoading(false), 400);
//       return () => clearTimeout(timer);
//     }
//   }, [profileLoading, companyLoading, setAppLoading]);

//   return <>{children}</>;
// };

// // ── LoaderOverlay ─────────────────────────────────────────────────────────────
// /**
//  * Branded loader — renders inside CompanyProvider so it can show the real
//  * company name and logo once that data has loaded. Sits on top of EarlyLoader
//  * (both share the same isAppLoading flag so only one is ever visible).
//  */
// const LoaderOverlay: React.FC = () => {
//   const { isAppLoading } = useAppLoading();
//   const { companyProfile } = useCompany();
//   const logoUrl = companyProfile?.logo || '/asset/images/logo/logo.png';

//   if (!isAppLoading) return null;

//   return (
//     <AppLoader
//       companyName={companyProfile?.companyName}
//       logoUrl={logoUrl}
//     />
//   );
// };

// // ── Smart default redirect based on role ─────────────────────────────────────
// const AdminDefaultRedirect: React.FC = () => {
//   const { userProfile, profileLoading } = useAuth();
//   if (profileLoading) return null;
//   const role = userProfile?.role as UserRole | undefined;
//   return <Navigate to={getDefaultPathForRole(role)} replace />;
// };

// // ── App ───────────────────────────────────────────────────────────────────────
// function App() {
//   return (
//     <QueryClientProvider client={queryClient}>
//       <AppThemeProvider>
//         <AppLoadingProvider>

//           {/*
//             ✅ EarlyLoader sits OUTSIDE AuthProvider & CompanyProvider.
//             It mounts immediately on first paint, eliminating the blank-screen
//             flash that occurred while the inner providers were still booting.
//           */}
//           <EarlyLoader />

//           <AuthProvider>
//             <CompanyProvider>
//               <AppReadyGate>

//                 {/*
//                   ✅ LoaderOverlay replaces EarlyLoader visually once
//                   CompanyProvider is available — it shows the real company
//                   name/logo. Both share isAppLoading so there's no double flash.
//                 */}
//                 <LoaderOverlay />

//                 <Router>
//                   <Routes>
//                     {/* Public routes */}
//                     <Route path="/" element={<AdminLogin />} />
//                     <Route path="/admin/login" element={<AdminLogin />} />
//                     <Route path="/admin/register" element={<AdminRegister />} />
//                     <Route path="/admin/forgot-password" element={<ForgotPassword />} />

//                     {/* Protected admin routes */}
//                     <Route path="/admin" element={
//                       <ProtectedRoute>
//                         <AdminLayout />
//                       </ProtectedRoute>
//                     }>
//                       {/* Smart redirect: goes to first allowed page for the user's role */}
//                       <Route index element={<AdminDefaultRedirect />} />

//                       {/* Each route is individually guarded by RoleProtectedRoute */}
//                       <Route path="dashboard" element={
//                         <RoleProtectedRoute path="/admin/dashboard">
//                           <Dashboard />
//                         </RoleProtectedRoute>
//                       } />
//                       <Route path="users" element={
//                         <RoleProtectedRoute path="/admin/users">
//                           <UserManagement />
//                         </RoleProtectedRoute>
//                       } />
//                       <Route path="roles" element={
//                         <RoleProtectedRoute path="/admin/roles">
//                           <RoleManagement />
//                         </RoleProtectedRoute>
//                       } />
//                       <Route path="orders" element={
//                         <RoleProtectedRoute path="/admin/orders">
//                           <OrdersPage />
//                         </RoleProtectedRoute>
//                       } />
//                       <Route path="return" element={
//                         <RoleProtectedRoute path="/admin/return">
//                           <ReturnManagement />
//                         </RoleProtectedRoute>
//                       } />
//                       <Route path="products" element={
//                         <RoleProtectedRoute path="/admin/products">
//                           <ProductsPage />
//                         </RoleProtectedRoute>
//                       } />
//                       <Route path="category" element={
//                         <RoleProtectedRoute path="/admin/category">
//                           <CategoryManagement />
//                         </RoleProtectedRoute>
//                       } />
//                       <Route path="variant" element={
//                         <RoleProtectedRoute path="/admin/variant">
//                           <VariantManagement />
//                         </RoleProtectedRoute>
//                       } />
//                       <Route path="brand" element={
//                         <RoleProtectedRoute path="/admin/brand">
//                           <BrandManagement />
//                         </RoleProtectedRoute>
//                       } />
//                       <Route path="warehouse" element={
//                         <RoleProtectedRoute path="/admin/warehouse">
//                           <WarehousesPage />
//                         </RoleProtectedRoute>
//                       } />
//                       <Route path="shipping" element={
//                         <RoleProtectedRoute path="/admin/shipping">
//                           <ShippingManagement />
//                         </RoleProtectedRoute>
//                       } />
//                       <Route path="purchase-order" element={
//                         <RoleProtectedRoute path="/admin/purchase-order">
//                           <PurchaseOrdersManagement />
//                         </RoleProtectedRoute>
//                       } />
//                       <Route path="inventory-return" element={
//                         <RoleProtectedRoute path="/admin/inventory-return">
//                           <ReturnsManagement />
//                         </RoleProtectedRoute>
//                       } />
//                       <Route path="suplier" element={
//                         <RoleProtectedRoute path="/admin/suplier">
//                           <SuppliersManagement />
//                         </RoleProtectedRoute>
//                       } />
//                       <Route path="reports" element={
//                         <RoleProtectedRoute path="/admin/reports">
//                           <ReportManagement />
//                         </RoleProtectedRoute>
//                       } />
//                       <Route path="messages" element={
//                         <RoleProtectedRoute path="/admin/messages">
//                           <ContactMessagesManagement />
//                         </RoleProtectedRoute>
//                       } />
//                       <Route path="reviews" element={
//                         <RoleProtectedRoute path="/admin/reviews">
//                           <ReviewsManagement />
//                         </RoleProtectedRoute>
//                       } />
//                       <Route path="blogs" element={
//                         <RoleProtectedRoute path="/admin/blogs">
//                           <BlogManagement />
//                         </RoleProtectedRoute>
//                       } />
//                       <Route path="newsletter" element={
//                         <RoleProtectedRoute path="/admin/newsletter">
//                           <NewsletterManagement />
//                         </RoleProtectedRoute>
//                       } />
//                       <Route path="banner" element={
//                         <RoleProtectedRoute path="/admin/banner">
//                           <BannerManagement />
//                         </RoleProtectedRoute>
//                       } />

//                       {/* Always-allowed routes (no role check needed) */}
//                       <Route path="profile" element={<UserProfile />} />
//                       <Route path="settings" element={<CompanyProfile />} />

//                       {/* Fallback inside admin */}
//                       <Route path="404" element={<AdminDashboard />} />
//                       <Route path="*" element={<AdminDefaultRedirect />} />
//                     </Route>

//                     {/* Global fallback */}
//                     <Route path="*" element={<Navigate to="/" replace />} />
//                   </Routes>
//                 </Router>

//               </AppReadyGate>
//             </CompanyProvider>
//           </AuthProvider>

//         </AppLoadingProvider>
//       </AppThemeProvider>
//       <ReactQueryDevtools initialIsOpen={false} />
//     </QueryClientProvider>
//   );
// }

// export default App;


// // import React, { useEffect } from 'react';
// // import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
// // import { QueryClientProvider } from '@tanstack/react-query';
// // import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
// // import { AuthProvider, useAuth } from './contexts/AuthContext';
// // import { CompanyProvider, useCompany } from './contexts/CompanyContext';
// // import { AppThemeProvider } from './theme/ThemeProvider';
// // import { AppLoadingProvider, useAppLoading } from './contexts/appLoadingContext';
// // import ProtectedRoute from './components/ProtectedRoute';
// // import { queryClient } from './lib/queryClient';
// // import AppLoader from './admin/components/appLoader';

// // // Admin Components
// // import AdminLayout from './admin/components/AdminLayout';
// // import AdminLogin from './admin/pages/Login';
// // import AdminRegister from './admin/pages/Register';
// // import AdminDashboard from './admin/pages/Dashboard';

// // // Web Components
// // import ProductsPage from './admin/pages/product/Products';
// // import OrdersPage from './admin/pages/order/Orders';
// // import CategoryManagement from './admin/pages/category/CategoryManagement';
// // import VariantManagement from './admin/pages/variant/VariantManagement';
// // import WarehousesPage from './admin/pages/warehouse/Warehouses';
// // import UserManagement from './admin/pages/users/users';
// // import InitialAdminSetup from './admin/pages/users/InitialAdminSetup';
// // import UserProfile from './admin/pages/users/userProfile';
// // import Dashboard from './admin/pages/dashboard/dashboard';
// // import CompanyProfile from './admin/pages/company/CompanyProfile';
// // import BlogManagement from './admin/pages/blogs/BlogManagement';
// // import ShippingManagement from './admin/pages/shipping/ShippingManagement';
// // import ContactMessagesManagement from './admin/pages/messages/ContactMessagesManagement';
// // import ReviewsManagement from './admin/pages/review/ReviewsManagement';
// // import PurchaseOrdersManagement from './admin/pages/purchase-orders/PurchaseOrdersManagement';
// // import SuppliersManagement from './admin/pages/supplier/SuppliersManagement';
// // import BrandManagement from './admin/pages/brand/BrandManagement';
// // import ReportManagement from './admin/pages/report/ReportManagement';
// // import ReturnsManagement from './admin/pages/purchase-orders/ReturnsManagement';
// // import ForgotPassword from './admin/pages/Forgotpasswordfirebase';
// // import ReturnManagement from './admin/pages/return/ReturnManagement';
// // import NewsletterManagement from './admin/pages/Newsletter/Newslettermanagement';

// // // ── Inner component: watches auth + company state and hides the loader ────────

// // const AppReadyGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
// //   const { profileLoading } = useAuth();
// //   const { loading: companyLoading } = useCompany();
// //   const { isAppLoading, setAppLoading } = useAppLoading();

// //   useEffect(() => {
// //     // Both auth profile AND company profile must finish loading
// //     if (!profileLoading && !companyLoading) {
// //       // Small delay so the loader doesn't flash out too abruptly
// //       const timer = setTimeout(() => setAppLoading(false), 400);
// //       return () => clearTimeout(timer);
// //     }
// //   }, [profileLoading, companyLoading, setAppLoading]);

// //   return <>{children}</>;
// // };

// // // ── Root loader overlay: reads company info to personalise the spinner ────────

// // const LoaderOverlay: React.FC = () => {
// //   const { isAppLoading } = useAppLoading();
// //   const { companyProfile } = useCompany();
// //   const logoUrl = 'asset/images/logo/logo.png'

// //   if (!isAppLoading) return null;

// //   return (
// //     <AppLoader
// //       companyName={companyProfile?.companyName}
// //       logoUrl={logoUrl ? logoUrl : companyProfile?.logo}
// //     />
// //   );
// // };

// // // ── App ───────────────────────────────────────────────────────────────────────

// // function App() {
// //   return (
// //     // ── QueryClientProvider must be the outermost wrapper so every hook
// //     // inside the tree (including those inside AuthProvider / CompanyProvider)
// //     // can access the shared React Query cache.
// //     <QueryClientProvider client={queryClient}>
// //       <AppThemeProvider>
// //         {/*
// //           AppLoadingProvider sits outside AuthProvider / CompanyProvider so it
// //           can be read anywhere in the tree, but the actual "done" signal is
// //           driven from inside AppReadyGate (which sits inside both providers).
// //         */}
// //         <AppLoadingProvider>
// //           <AuthProvider>
// //             <CompanyProvider>

// //               {/* Watches loading flags and flips isAppLoading → false */}
// //               <AppReadyGate>

// //                 {/* Full-screen branded spinner — renders on top of everything */}
// //                 <LoaderOverlay />

// //                 <Router>
// //                   <Routes>
// //                     {/* Web Routes */}
// //                     <Route path="/" element={<AdminLogin />} />

// //                     {/* Admin Authentication Routes */}
// //                     <Route path="/admin/login" element={<AdminLogin />} />
// //                     <Route path="/admin/register" element={<AdminRegister />} />
// //                     <Route path="/admin/forgot-password" element={<ForgotPassword />} />

// //                     {/* Protected Admin Routes */}
// //                     <Route path="/admin" element={
// //                       <ProtectedRoute>
// //                         <AdminLayout />
// //                       </ProtectedRoute>
// //                     }>
// //                       <Route index element={<Navigate to="/admin/dashboard" replace />} />
// //                       <Route path="dashboard" element={<Dashboard />} />
// //                       <Route path="products" element={<ProductsPage />} />
// //                       <Route path="orders" element={<OrdersPage />} />
// //                       <Route path="category" element={<CategoryManagement />} />
// //                       <Route path="variant" element={<VariantManagement />} />
// //                       <Route path="warehouse" element={<WarehousesPage />} />
// //                       <Route path="users" element={<UserManagement />} />
// //                       <Route path="blog" element={<AdminDashboard />} />
// //                       <Route path="signin" element={<AdminDashboard />} />
// //                       <Route path="profile" element={<UserProfile />} />
// //                       <Route path="settings" element={<CompanyProfile />} />
// //                       <Route path="blogs" element={<BlogManagement />} />
// //                       <Route path="shipping" element={<ShippingManagement />} />
// //                       <Route path="messages" element={<ContactMessagesManagement />} />
// //                       <Route path="reviews" element={<ReviewsManagement />} />
// //                       <Route path="purchase-order" element={<PurchaseOrdersManagement />} />
// //                       <Route path="inventory-return" element={<ReturnsManagement />} />
// //                       <Route path="suplier" element={<SuppliersManagement />} />
// //                       <Route path="brand" element={<BrandManagement />} />
// //                       <Route path="return" element={<ReturnManagement />} />
// //                       <Route path="reports" element={<ReportManagement />} />
// //                       <Route path="newsletter" element={<NewsletterManagement />} />
// //                       <Route path="404" element={<AdminDashboard />} />
// //                     </Route>

// //                     {/* Fallback route */}
// //                     <Route path="*" element={<Navigate to="/" replace />} />
// //                   </Routes>
// //                 </Router>

// //               </AppReadyGate>
// //             </CompanyProvider>
// //           </AuthProvider>
// //         </AppLoadingProvider>
// //       </AppThemeProvider>

// //       {/* React Query Devtools — only renders in development builds.
// //           The floating icon in the bottom corner lets you inspect the cache,
// //           check query freshness, and manually trigger invalidations.
// //           Remove this line (or the package) before deploying to production. */}
// //       <ReactQueryDevtools initialIsOpen={false} />
// //     </QueryClientProvider>
// //   );
// // }

// // export default App;